import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import type { Reference } from "@frontend/lib/references";
import { loadReferences } from "@frontend/lib/references";

import { amendmentApi } from "../services";
import type {
  AmendmentBatchResult,
  AmendmentEmployeeRow,
  AmendmentPerPage,
  AmendmentSortDirection,
  AmendmentStatusChoice,
} from "../types";

export type AmendmentEmployeesState = {
  year: number;
  rows: AmendmentEmployeeRow[];
  total: number;
  page: number;
  perPage: AmendmentPerPage;
  search: string;
  responsable: number | null;
  status: string | null;
  direction: AmendmentSortDirection;
  responsables: Reference[];
  statuses: AmendmentStatusChoice[];
  selection: number[];
  loading: boolean;
  working: boolean;
  error: string | null;
  batch: AmendmentBatchResult | null;
};

export const initialAmendmentEmployeesState: AmendmentEmployeesState = {
  year: new Date().getFullYear(),
  rows: [],
  total: 0,
  page: 1,
  perPage: 25,
  search: "",
  responsable: null,
  status: null,
  direction: "asc",
  responsables: [],
  statuses: [],
  selection: [],
  loading: true,
  working: false,
  error: null,
  batch: null,
};

export const amendmentEmployeesAtom = atomWithImmer<AmendmentEmployeesState>(
  initialAmendmentEmployeesState,
);

export const amendmentEmployeesPageCountAtom = atom((get) => {
  const { total, perPage } = get(amendmentEmployeesAtom);

  return Math.max(1, Math.ceil(total / perPage));
});

const drawAtom = atom(0);

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export const loadAmendmentEmployeesAtom = atom(null, async (get, set) => {
  const draw = get(drawAtom) + 1;
  set(drawAtom, draw);

  const { year, search, responsable, status, direction, page, perPage } = get(
    amendmentEmployeesAtom,
  );

  set(amendmentEmployeesAtom, (state) => {
    state.loading = true;
    state.error = null;
  });

  try {
    const [loaded, responsables, statuses] = await Promise.all([
      amendmentApi.fetchEmployees(year, {
        search,
        responsable,
        status,
        direction,
        page,
        perPage,
      }),
      loadReferences("employeeAccountants", { closureWeeksYear: year }),
      amendmentApi.fetchStatuses(),
    ]);

    if (get(drawAtom) !== draw) {
      return;
    }

    set(amendmentEmployeesAtom, (state) => {
      state.rows = loaded.items;
      state.total = loaded.total;
      state.page = loaded.page;
      state.responsables = responsables;
      state.statuses = statuses;
      state.loading = false;
    });
  } catch (error: unknown) {
    if (get(drawAtom) !== draw) {
      return;
    }

    set(amendmentEmployeesAtom, (state) => {
      state.loading = false;
      state.error = messageOf(error);
    });
  }
});

export const setAmendmentEmployeesSearchAtom = atom(
  null,
  (_get, set, search: string) => {
    set(amendmentEmployeesAtom, (state) => {
      state.search = search;
      state.page = 1;
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const setAmendmentEmployeesResponsableAtom = atom(
  null,
  (_get, set, responsable: number | null) => {
    set(amendmentEmployeesAtom, (state) => {
      state.responsable = responsable;
      state.page = 1;
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const setAmendmentEmployeesStatusAtom = atom(
  null,
  (_get, set, status: string | null) => {
    set(amendmentEmployeesAtom, (state) => {
      state.status = status;
      state.page = 1;
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const setAmendmentEmployeesDirectionAtom = atom(
  null,
  (_get, set, direction: AmendmentSortDirection) => {
    set(amendmentEmployeesAtom, (state) => {
      state.direction = direction;
      state.page = 1;
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const setAmendmentEmployeesPerPageAtom = atom(
  null,
  (_get, set, perPage: AmendmentPerPage) => {
    set(amendmentEmployeesAtom, (state) => {
      state.perPage = perPage;
      state.page = 1;
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const setAmendmentEmployeesPageAtom = atom(
  null,
  (_get, set, page: number) => {
    set(amendmentEmployeesAtom, (state) => {
      state.page = page;
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const setAmendmentEmployeesYearAtom = atom(
  null,
  (_get, set, year: number) => {
    set(amendmentEmployeesAtom, (state) => {
      state.year = year;
      state.page = 1;
      state.selection = [];
    });
    void set(loadAmendmentEmployeesAtom);
  },
);

export const toggleAmendmentEmployeeSelectionAtom = atom(
  null,
  (_get, set, employeeId: number) => {
    set(amendmentEmployeesAtom, (state) => {
      state.selection = state.selection.includes(employeeId)
        ? state.selection.filter((id) => id !== employeeId)
        : [...state.selection, employeeId];
    });
  },
);

export const toggleAmendmentPageSelectionAtom = atom(
  null,
  (_get, set, selected: boolean) => {
    set(amendmentEmployeesAtom, (state) => {
      const onPage = state.rows.map((row) => row.employeeId);

      state.selection = selected
        ? [...new Set([...state.selection, ...onPage])]
        : state.selection.filter((id) => !onPage.includes(id));
    });
  },
);

export const clearAmendmentBatchAtom = atom(null, (_get, set) => {
  set(amendmentEmployeesAtom, (state) => {
    state.batch = null;
  });
});

type SetState = (update: (state: AmendmentEmployeesState) => void) => void;

async function run(
  setState: SetState,
  action: () => Promise<void>,
): Promise<boolean> {
  setState((state) => {
    state.working = true;
    state.error = null;
  });

  try {
    await action();

    return true;
  } catch (error: unknown) {
    setState((state) => {
      state.error = messageOf(error);
    });

    return false;
  } finally {
    setState((state) => {
      state.working = false;
    });
  }
}

export const generateAmendmentAtom = atom(
  null,
  async (get, set, employeeId: number, generatedOn: string) => {
    const { year } = get(amendmentEmployeesAtom);
    const setState: SetState = (update) => set(amendmentEmployeesAtom, update);

    if (
      await run(setState, () =>
        amendmentApi.generate(year, employeeId, generatedOn).then(),
      )
    ) {
      void set(loadAmendmentEmployeesAtom);
    }
  },
);

export const changeAmendmentStatusAtom = atom(
  null,
  async (_get, set, amendmentId: number, status: string) => {
    const setState: SetState = (update) => set(amendmentEmployeesAtom, update);

    if (
      await run(setState, () =>
        amendmentApi.changeStatus(amendmentId, status).then(),
      )
    ) {
      void set(loadAmendmentEmployeesAtom);
    }
  },
);

export const generateAmendmentBatchAtom = atom(
  null,
  async (get, set, generatedOn: string) => {
    const { year, selection } = get(amendmentEmployeesAtom);
    const setState: SetState = (update) => set(amendmentEmployeesAtom, update);

    const done = await run(setState, async () => {
      const result = await amendmentApi.generateBatch(
        year,
        selection,
        generatedOn,
      );

      setState((state) => {
        state.batch = result;
      });
    });

    if (done) {
      void set(loadAmendmentEmployeesAtom);
    }
  },
);

export const previewAmendmentBundleAtom = atom(null, async (get, set) => {
  const { year, selection } = get(amendmentEmployeesAtom);
  const setState: SetState = (update) => set(amendmentEmployeesAtom, update);

  await run(setState, async () => {
    const result = await amendmentApi.previewBundle(year, selection);

    setState((state) => {
      state.batch = result;
    });
  });
});
